<script lang="ts">
  import type { Countdown } from '../lib/storage';
  import { decompose, parseFormatUnits, turnsYearsFor, visibleUnitsFor } from '../lib/countdown';
  import { colorVars } from '../lib/color';
  import { formatDueDate, unitLabel } from '../lib/format';
  import { buildShareURL, buildCalendarURL } from '../lib/share';
  import { navigate } from '../lib/router';
  import EmojiBackground from './EmojiBackground.svelte';

  type Props = { countdown: Countdown };
  let { countdown }: Props = $props();

  let now = $state(new Date());
  $effect(() => {
    const id = setInterval(() => { now = new Date(); }, 1000);
    return () => clearInterval(id);
  });

  const vars = $derived(colorVars(countdown.colorHex));
  const dueDate = $derived(new Date(countdown.dueDate));

  /// Tint iOS Safari's URL bar (and the status-bar tint in standalone PWA
  /// mode) to match the countdown's hero color.
  $effect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    const previous = meta.content;
    meta.content = vars.color;
    return () => {
      meta.content = previous;
    };
  });

  /// Paint the body itself with the countdown color (not just the inner
  /// `.root`) so the bleed extends behind Safari's URL bar and the iOS
  /// status bar.
  $effect(() => {
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = vars.color;
    return () => {
      document.body.style.backgroundColor = previous;
    };
  });

  const enabledUnits = $derived(parseFormatUnits(countdown.formatUnits));
  const isPast = $derived(dueDate < now);
  const parts = $derived(
    decompose(isPast ? dueDate : now, isPast ? now : dueDate, enabledUnits, countdown.countOnlyWeekdays ?? false),
  );
  const visibleUnits = $derived(visibleUnitsFor(enabledUnits, parts));
  const dueLabel = $derived(formatDueDate(dueDate, countdown.allDay));
  const turnsYears = $derived(turnsYearsFor(countdown.kind, countdown.originYear, dueDate));

  let sharing = $state(false);

  function onAddToCalendar() {
    location.href = buildCalendarURL(countdown);
  }

  async function share() {
    if (sharing) return;
    sharing = true;
    try {
      const url = buildShareURL(countdown);
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: countdown.title, url });
          return;
        } catch {
          // user cancelled or share unavailable — fall through to clipboard
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        flash('Link copied');
      } catch {
        window.prompt('Copy this link:', url);
      }
    } catch {
      flash("Couldn't share");
    } finally {
      sharing = false;
    }
  }

  let flashText = $state<string | null>(null);
  let flashTimer: ReturnType<typeof setTimeout> | undefined;
  function flash(text: string) {
    flashText = text;
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      flashText = null;
    }, 1600);
  }
</script>

<div
  class="root"
  style="--bg: {vars.color}; --text: {vars.textColor}; --unit-bg: {vars.unitBg};"
>
  <EmojiBackground emoji={countdown.emoji} />

  <div class="topbar">
    <button class="icon" onclick={() => navigate('/')} aria-label="Back">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <div class="spacer"></div>
    <button class="text-button" onclick={() => navigate(`/c/${countdown.id}/edit`)}>
      Edit
    </button>
  </div>

  <div class="card">
    <div class="emoji">{countdown.emoji}</div>
    <div class="title">{countdown.title}</div>
    {#if turnsYears !== null}
      <div class="turns">Turns {turnsYears}</div>
    {/if}
    <div class="due-date">
      {#if isPast}<span class="past-badge">Past</span>{/if}{dueLabel}
    </div>
    <div class="grid" style="--units: {visibleUnits.length}">
      {#each visibleUnits as unit (unit)}
        <div class="unit">
          <span class="unit-value">{parts[unit]}</span>
          <span class="unit-label">{unitLabel(unit, parts[unit], countdown.countOnlyWeekdays ?? false)}</span>
        </div>
      {/each}
    </div>
  </div>

  <div class="actions">
    <button class="action-pill" onclick={onAddToCalendar}>Add to Calendar</button>
    <button class="action-pill" onclick={share} disabled={sharing}>Share Link</button>
  </div>

  {#if flashText}
    <div class="flash">{flashText}</div>
  {/if}
</div>

<style>
  .root {
    min-height: 100dvh;
    background-color: var(--bg);
    color: var(--text);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 24px max(40px, env(safe-area-inset-bottom));
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  }

  .topbar {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 720px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: max(12px, env(safe-area-inset-top)) 0 12px;
  }
  .topbar .spacer {
    flex: 1;
  }

  .icon {
    appearance: none;
    background: rgba(0, 0, 0, 0.15);
    color: inherit;
    border: 0;
    border-radius: 999px;
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  .icon:hover { background: rgba(0, 0, 0, 0.22); }
  .icon svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* Same translucent-blur treatment as the back button — pill-shaped so
     "Edit" text fits without cramping. Height matches .icon (40px) so the
     topbar reads as a single row of consistent controls. */
  .text-button {
    appearance: none;
    background: rgba(0, 0, 0, 0.15);
    color: inherit;
    border: 0;
    border-radius: 999px;
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  .text-button:hover { background: rgba(0, 0, 0, 0.22); }

  .card {
    position: relative;
    z-index: 1;
    margin-top: clamp(24px, 6vh, 60px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: fit-content;
    min-width: min(280px, calc(100vw - 48px));
    max-width: min(420px, calc(100vw - 48px));
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-radius: 28px;
    padding: 40px 32px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }

  .emoji {
    font-size: 80px;
    line-height: 1;
  }
  .title {
    font-size: 2rem;
    font-weight: 700;
    text-align: center;
    line-height: 1.2;
  }
  .turns {
    font-size: 1.1rem;
    font-weight: 500;
    opacity: 0.85;
    text-align: center;
  }
  .due-date {
    font-size: 0.9rem;
    opacity: 0.7;
    text-align: center;
  }
  .past-badge {
    display: inline-block;
    background: rgba(0, 0, 0, 0.28);
    color: inherit;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 999px;
    margin-right: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    vertical-align: middle;
    opacity: 1;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(var(--units, 4), minmax(0, 80px));
    gap: 10px;
    margin-top: 8px;
    justify-content: center;
  }
  .unit {
    background: var(--unit-bg);
    border-radius: 14px;
    padding: 16px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  .unit-value {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .unit-label {
    font-size: 0.65rem;
    font-weight: 600;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Pills sit below the card with the same blur/translucent treatment so
     they read as part of the hero, not separate UI. Labels (not icons) so
     "Add to Calendar" and "Share Link" leave no doubt about their action. */
  .actions {
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    margin-top: 24px;
  }
  .action-pill {
    appearance: none;
    border: 0;
    padding: 12px 22px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: inherit;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .action-pill:hover { background: rgba(0, 0, 0, 0.22); }
  .action-pill[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .flash {
    position: fixed;
    bottom: max(24px, env(safe-area-inset-bottom));
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.78);
    color: white;
    padding: 10px 18px;
    border-radius: 999px;
    font-size: 0.9rem;
    z-index: 10;
    pointer-events: none;
  }
</style>
